/* hook reusable del polling para obtener el estado de un pago por qr */
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../services/api";

export const usePolling = () => {
  const [pollingInterval, setPollingInterval] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentReference, setPaymentReference] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const pollingRef = useRef(null);
  const timeoutRef = useRef(null);

  const MAX_POLLING_ATTEMPTS = 45; // 45 intentos * 8 segs = 6 mins totales
  const POLLING_INTERVAL = 8000; //cada 8 segs
  const TIMEOUT_DURATION = 5 * 60 * 1000; //5 mins

  const checkPaymentStatus = useCallback(async (reference, provider = "pvs") => {
    if (!reference) {
      console.error("Referencia de pago no proporcionada.");
      return null;
    }

    try {
      let response;

      switch (provider) {
        case "pvs":
          response = await api.pvs.getQRPaymentStatus(reference);
          if (response.status === "success") {
            const isApproved = response.data?.estado === "APPROVED" ||
              response.data?.pvs_status === "APPROVED" ||
              response.data?.local_status === "APPROVED";

            const isRejected = response.data?.estado === "REJECTED" ||
              response.data?.estado === "REVERSE" ||
              response.data?.pvs_status === "REJECTED";

            if (isApproved) {
              return { status: "APROBADO", data: response.data };
            } else if (isRejected) {
              return { status: "RECHAZADO", data: response.data };
            }
            return { status: "PENDIENTE", data: response.data };
          }
          break;

        case 'clickmacro':
          response = await api.clickmacro.getPaymentStatus(reference);
          if (response.status === "success") {
            const estado = response.recibo?.estado;
            if (estado === "APROBADO" || estado === "REALIZADA") {
              return { status: "APROBADO", data: response.data };
            } else if (estado === "PENDIENTE") {
              return { status: "PENDIENTE", data: response.data };
            } else {
              console.log(` [CLICKMACRO] Estado no reconocido: "${estado}"`);
            }

            return { status: estado || "PENDIENTE", data: response.data };
          } else {
            console.log(`❌ [CLICKMACRO] API no respondió success`);
          }
          break;

        default:
          console.warn(`Proveedor desconocido: ${provider}`);
          return null;
      }
    }
    catch (error) {
      console.error("Error al verificar el estado del pago: ", error);
      return null;
    }

    return null;
  }, []);

  const startPolling = useCallback((reference, provider = "pvs", options = {}) => {
    const {
      onSuccess,
      onRejected,
      onTimeout,
      onMaxAttemptsReached,
      maxAttemps = MAX_POLLING_ATTEMPTS,
      interval = POLLING_INTERVAL,
      timeout = TIMEOUT_DURATION,
      enableTimeout = true
    } = options;

    stopPolling();

    setPaymentReference(reference);
    setPollingAttempts(0);
    setIsPolling(true);
    setPaymentStatus("PENDIENTE");

    checkPaymentStatus(reference, provider).then(result => {
      if (result?.status === "APROBADO") {
        setPaymentStatus("APROBADO");
        setIsPolling(false);
        onSuccess?.(result);
        return;
      } else if (result?.status === "RECHAZADO") {
        setPaymentStatus("RECHAZADO");
        setIsPolling(false);
        onRejected?.(result);
        return;
      }
    });

    pollingRef.current = setInterval(async () => {
      setPollingAttempts(prev => {
        const newAttempt = prev + 1;
        console.log(`Polling intento #${newAttempt} para ${reference}`);

        if (newAttempt >= maxAttemps) {
          clearInterval(pollingRef.current);
          setIsPolling(false);
          setPaymentStatus("EXPIRADO");
          onMaxAttemptsReached?.();
          return newAttempt;
        }

        return newAttempt;
      });

      const result = await checkPaymentStatus(reference, provider);

      if (result?.status === "APROBADO") {
        setPaymentStatus("APROBADO");
        setIsPolling(false);
        clearInterval(pollingRef.current);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        onSuccess?.(result);
        return;
      } else if (result?.status === "RECHAZADO") {
        setPaymentStatus("RECHAZADO");
        setIsPolling(false);
        clearInterval(pollingRef.current);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        onRejected?.(result);
        return;
      }
    }, interval);

    if (enableTimeout && timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        if (isPolling) {
          const continuarPolling = window.confirm(
            "No se ha recibido ningún pago en los últimos 5 minutos.\n\n¿Deseas seguir esperando?\n\nPresiona 'Aceptar' para seguir esperando o 'Cancelar' para cancelar la operación.\n\nSi ya realizaste el pago, te recomendamos continuar esperando unos minutos más antes de cancelar."
          );

          if (continuarPolling) {
            timeoutRef.current = setTimeout(() => {
              onTimeout?.();
            }, timeout);
          } else {
            stopPolling();
            onTimeout?.();
          }
        }
      }, timeout);
    }

    setPollingInterval(pollingRef.current);
  }, [checkPaymentStatus, isPolling]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setPollingInterval(null);
    setIsPolling(false);
    setPollingAttempts(0);
  }, []);

  const resetPolling = useCallback(() => {
    stopPolling();
    setPaymentStatus(null);
    setPaymentReference(null);
  }, [stopPolling]);

  const restartTimeout = useCallback((timeoutDuration = TIMEOUT_DURATION, onTimeout) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (isPolling) {
        const continuarPolling = window.confirm(
          "No se ha recibido ningún pago en los últimos 5 minutos.\n\n¿Deseas seguir esperando?\n\nPresiona 'Aceptar' para seguir esperando o 'Cancelar' para cancelar la operación.\n\nSi ya realizaste el pago, te recomendamos continuar esperando unos minutos más antes de cancelar."
        );

        if (continuarPolling) {
          restartTimeout(timeoutDuration, onTimeout);
        } else {
          stopPolling();
          onTimeout?.();
        }
      }
    }, timeoutDuration);
  }, [isPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    // estados
    isPolling,
    paymentStatus,
    paymentReference,
    pollingAttempts,

    // funciones del polling
    startPolling,
    stopPolling,
    resetPolling,
    checkPaymentStatus,
    restartTimeout,

    // settings
    MAX_POLLING_ATTEMPTS,
    POLLING_INTERVAL,
    TIMEOUT_DURATION
  };
};